import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDiamondExclamation } from "@fortawesome/sharp-solid-svg-icons"
import type {
  HeatmapProps,
  HeatmapCellOwnerState,
} from "@mui/x-charts-pro/Heatmap"
import { useXScale, useYScale } from "@mui/x-charts-pro"
import { Fragment } from "react"
import {
  CELL_HEIGHT,
  CELL_WIDTH,
  ICON_SIZE,
  WASTEWATER_HEATMAP_CELL_COLORS,
  WastewaterCategory,
  type HeatmapDataPointDetails,
} from "./wastewaterHeatmapConstants"

type CellProps = NonNullable<NonNullable<HeatmapProps["slotProps"]>["cell"]>

type HeatmapCellProps = CellProps & {
  ownerState: HeatmapCellOwnerState
  getAnomalyDetails?: (
    xIndex: number,
    yIndex: number
  ) => HeatmapDataPointDetails | undefined
}

export const HeatmapCell = (props: HeatmapCellProps) => {
  const { x, y, width, height, ownerState, getAnomalyDetails, ...other } = props
  const xScale = useXScale()
  const yScale = useYScale()
  if (x == null || y == null || width == null || height == null) {
    return null
  }

  const xDomain = xScale.domain() as number[]
  const yDomain = yScale.domain() as number[]
  const xIndex = xDomain.findIndex(d => xScale(d) === x)
  const yIndex = yDomain.findIndex(d => yScale(d) === y)
  const anomalyDetails =
    xIndex >= 0 && yIndex >= 0 ? getAnomalyDetails?.(xIndex, yIndex) : undefined
  const isAnomaly = anomalyDetails?.isAnomaly
  const isSelected = anomalyDetails?.isSelected
  const showBorder =
    anomalyDetails?.value === WastewaterCategory.NOT_CALCULATED ||
    anomalyDetails?.value === WastewaterCategory.NO_DATA
  const accentColor = isSelected
    ? "rgba(55, 178, 255, 1)"
    : WASTEWATER_HEATMAP_CELL_COLORS[
        anomalyDetails?.value ?? WastewaterCategory.NO_DATA
      ].light
  const xOffset = (width - CELL_WIDTH) / 2
  const yOffset = (height - CELL_HEIGHT) / 2
  const borderWidth = isAnomaly ? 2 : 0.5
  return (
    <Fragment>
      <rect
        {...other}
        x={x + xOffset}
        y={y + yOffset}
        width={CELL_WIDTH}
        height={CELL_HEIGHT}
        rx={1}
        ry={1}
        fill={ownerState.color}
        clipPath={ownerState.isHighlighted ? undefined : "inset(0px round 1px)"}
      />
      {(isAnomaly || showBorder) && (
        <rect
          x={x + xOffset + 1}
          y={y + yOffset + 1}
          width={CELL_WIDTH - borderWidth}
          height={CELL_HEIGHT - borderWidth}
          rx={1}
          ry={1}
          fill="none"
          stroke={accentColor}
          strokeWidth={borderWidth}
          style={{ pointerEvents: "none" }}
        />
      )}
      {isAnomaly && (
        <FontAwesomeIcon
          icon={faDiamondExclamation}
          x={x + xOffset + (CELL_WIDTH - ICON_SIZE) / 2}
          y={y + yOffset + (CELL_HEIGHT - ICON_SIZE) / 2}
          width={ICON_SIZE}
          height={ICON_SIZE}
          style={{ pointerEvents: "none", color: accentColor }}
        />
      )}
    </Fragment>
  )
}
